/**
 * Pantalla de verificación OTP por email (segundo factor).
 *
 * Se renderiza cuando el Router detecta el gate MFA_REQUIRED: el usuario ya
 * pasó el primer factor (email/password) pero su método es 'email' y aún no
 * verificó el código de ESTE login. IMPORTANTE: mientras el gate esté activo
 * las Security Rules bloquean Firestore, así que esta pantalla solo habla
 * con las callables (sendLoginOtp / verifyLoginOtp).
 *
 * Al verificar, la acción refresca el token (llega el claim mfaAuthTime) y
 * `onVerified()` le pide al Router re-evaluar la sesión → carga de roles →
 * LOGGEDIN.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Input, Button, notification } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';

import { mfa } from '../../actions/auth/mfa';
import { logout } from '../../actions/auth/logout';
import { MAIL_SENDER } from '../../lib/mail_sender';
import '../styles/login.scss';

const FormItem = Form.Item;
const LogoCompany = 'https://firebasestorage.googleapis.com/v0/b/gestor-documental-67434.appspot.com/o/Cabezote_Web_CR.gif?alt=media&token=71d4c061-1cd3-4ed7-b23c-ee25f5c2c916';

export const MfaVerifyEmail = ({ onVerified }) => {

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [form] = Form.useForm();

  // Evita doble envío por re-render (React 17 sin StrictMode igual protege
  // contra remontajes rápidos del Router)
  const sentRef = useRef(false);

  const sendCode = async () => {
    setSending(true);
    try {
      const res = await dispatch( mfa.sendOtp('login') );
      setMaskedEmail(res?.email || null);
      setCooldown(res?.cooldownSeconds || 60);
    } catch ( error ) {
      // resource-exhausted = ya hay un código vigente enviado hace <60s
      // (p. ej. tras un F5): no es un fallo, solo activamos el cooldown.
      setCooldown(60);
      notification.info({
        message: 'Código enviado',
        description: error?.message || 'Ya se envió un código recientemente. Revise su correo',
        placement: 'topRight'
      });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown del botón de reenvío
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const onVerify = async values => {
    setLoading(true);
    try {
      await dispatch( mfa.verifyOtp( values.code, 'login' ) );
      onVerified && onVerified();
    } catch ( error ) {
      notification.warning({
        message: 'No se pudo verificar',
        description: error?.message || 'Código incorrecto',
        placement: 'topRight'
      });
      setLoading(false);
    }
  };

  return (
    <div className = 'cuerpo cuerpo-mfa' >

      <div className = 'imagenInicio' >
        <img alt = "logo" src = { LogoCompany } />
      </div>
      <div className = 'login login-mfa' >

        <Form form = { form } className = "login-form" onFinish = { onVerify } >

          <h3> Verificación en 2 pasos </h3>
          <p>
            Le llegará un correo con un código de 6 dígitos de parte
            de <strong>{ MAIL_SENDER }</strong> a su correo
            registrado{ maskedEmail ? <> <strong>{ maskedEmail }</strong></> : null }.
            Si no lo ve, revise la carpeta de spam.
          </p>

          <FormItem name = "code"
            rules = {[
              { required: true, message: 'Debe ingresar el código' },
              { pattern: /^[0-9]{6}$/, message: 'El código tiene 6 dígitos' }
            ]} >
            <Input
              autoFocus
              maxLength = { 6 }
              inputMode = "numeric"
              placeholder = "000000"
              prefix = { <SafetyOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> } />
          </FormItem>

          <FormItem>
            <Button type = "primary" htmlType = "submit" className = "login-form-button" loading = { loading } >
              Verificar
            </Button>
          </FormItem>

          <FormItem>
            <Button type = "link" style = {{ padding: 0 }} disabled = { cooldown > 0 } loading = { sending }
              onClick = { sendCode } >
              { cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código' }
            </Button>
            <Button type = "link" className = "login-form-forgot"
              onClick = { () => dispatch( logout() ) } >
              Cancelar
            </Button>
          </FormItem>

        </Form>

      </div>
    </div>
  );
};
